// ============================================================================
// Vista: empresa-suspendida — Panel Cliente
// Se muestra cuando la empresa está en estado que impide operar normalmente.
// Regla 15: mensajes específicos según estado + planes para prueba_terminada.
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';

import { cerrarSesion, obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

function moneda(valor) {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  }).format(Number(valor ?? 0));
}

export async function vistaEmpresaSuspendida(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const empresa = obtenerEstadoSesion().empresa;
  const estado = empresa?.estado ?? 'desconocido';
  const condicion = empresa?.condicion ?? 'trial';

  const mensajes = {
    prueba_terminada: {
      titulo: 'Tu prueba ha terminado',
      descripcion:
        'Tu periodo de prueba de 7 días ha finalizado. Para continuar usando la plataforma, ' +
        'elige un plan y envía tu comprobante de pago.',
      tono: 'alerta',
      mostrarPlanes: true,
    },
    vencida: {
      titulo: 'Suscripción vencida',
      descripcion:
        'Tu suscripción ha expirado. Envía tu comprobante de pago para reactivar tu servicio. ' +
        'La tienda es invisible para tus clientes hasta que se regularice el pago.',
      tono: 'peligro',
      mostrarPagos: true,
    },
    suspendida: {
      titulo: 'Tienda suspendida',
      descripcion:
        'Tu tienda ha sido suspendida por falta de pago prolongada. ' +
        'Tienes 30 días para reactivar antes de la eliminación permanente. ' +
        'Contacta al soporte para más información.',
      tono: 'peligro',
      mostrarPagos: false,
    },
    dada_de_baja: {
      titulo: 'Servicio dado de baja',
      descripcion: 'Tu servicio ha sido dado de baja y ya no está disponible.',
      tono: 'peligro',
    },
  };

  const info = mensajes[estado] ?? {
    titulo: 'Servicio no disponible',
    descripcion: 'Tu tienda no está disponible en este momento.',
    tono: 'peligro',
  };

  let planesHtml = '';
  if (info.mostrarPlanes) {
    const { data: planes } = await supabaseClient()
      .from('plan')
      .select('id, nombre, precio_mensual')
      .eq('estado', 'activo')
      .order('precio_mensual', { ascending: true });

    if (planes && planes.length > 0) {
      planesHtml = `
        <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(180px, 1fr)); gap:12px; margin-top:20px;">
          ${planes
            .map(
              (p) => `
            <div class="pf-tarjeta" style="padding:16px; text-align:center; border:2px solid var(--pf-color-borde, #e5e7eb); border-radius:12px;">
              <p style="margin:0; font-weight:700; font-size:1rem;">${p.nombre}</p>
              <p style="margin:8px 0 0; font-size:1.25rem; font-weight:700; color:var(--pf-color-primario, #2563eb);">
                ${moneda(p.precio_mensual)}<span style="font-size:0.75rem; font-weight:400;">/mes</span>
              </p>
            </div>`,
            )
            .join('')}
        </div>
      `;
    }
  }

  contenedorRaiz.innerHTML = `
    <div class="pf-login">
      <div class="pf-tarjeta pf-login__tarjeta" style="text-align:center; max-width:520px;">
        <div style="margin-bottom:24px;">
          <pf-badge-estado tono="${info.tono}" style="font-size:1.1rem;">
            ${info.titulo}
          </pf-badge-estado>
        </div>
        <h1 class="pf-login__titulo">${info.titulo}</h1>
        <p class="pf-texto-suave" style="margin-bottom:16px; line-height:1.6;">
          ${info.descripcion}
        </p>
        <p class="pf-texto-suave pf-texto-sm" style="margin-bottom:24px;">
          Estado: <strong>${estado}</strong> · Condición: <strong>${condicion}</strong>
          ${empresa?.plan?.nombre ? ` · Plan actual: <strong>${empresa.plan.nombre}</strong>` : ''}
        </p>
        ${planesHtml}
        <div style="display:flex; flex-direction:column; gap:12px; align-items:center; margin-top:24px;">
          ${
            estado === 'prueba_terminada'
              ? `
            <a href="#/cambiar-plan" class="pf-boton pf-boton--primario" style="width:100%; max-width:280px; text-align:center;">
              Elegir plan
            </a>
          `
              : ''
          }
          ${
            info.mostrarPagos
              ? `
            <a href="#/pagos" class="pf-boton pf-boton--primario" style="width:100%; max-width:280px; text-align:center;">
              Enviar comprobante de pago
            </a>
          `
              : ''
          }
          <button type="button" class="pf-boton pf-boton--secundario" id="pf-susp-salir" style="width:100%; max-width:280px;">
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  `;

  contenedorRaiz.querySelector('#pf-susp-salir').addEventListener('click', async () => {
    await cerrarSesion();
  });
}
